<template>
  <div v-if="loadOrdersFilter.startDate && loadOrdersFilter.endDate">
    <load-orders-filter
      @filter-load-orders="getAgentList"
      @download-pdf="emitDownloadPdf"
      listType="agent"
      isList
      hasPdf
      :excelData="agentList"
      :excelFields="excelFields"
    />
    <v-card elevation="24" class="mt-1">
      <v-card-title>
      <v-spacer/>
      <v-btn text class="mb-6 mr-3" @click="clearFilters">{{ $t('clearFilters') }}</v-btn>
      <v-icon class="mb-6 mr-3" @click="getAgentList">{{ icons.mdiReload }}</v-icon>
      <v-text-field
        v-model="search"
        :prepend-inner-icon="icons.mdiMagnify"
        :label="$t('aSearch')"
        counter="50"
        maxlength="50"
        clearable
        outlined
        rounded
        dense
        @change="getAgentList"
      />
      </v-card-title>
      <v-card-text>
        <v-data-table
          dense
          class="list"
          :headers="headers"
          :items="agentList"
          :loading="isLoading"
          :loading-text="$t('wot.loading')"
          :options.sync="options"
          :server-items-length="agentTotal"
          :mobile-breakpoint="0"
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': itemsPerPage
          }"
          :expanded.sync="expanded"
          item-key="bostamp"
          show-expand
          height="72vh"
          fixed-header
          :item-class="rowClasses"
        >
          <template v-slot:header.uMataFilter>
            <wot-filter-menu :items="getAgentListValue('u_mata')" fieldName="u_mata"/>
          </template>
          <template v-slot:header.uTiposFilter>
            <wot-filter-menu :items="getAgentListValue('u_tipos')" fieldName="u_tipos"/>
          </template>
          <template v-slot:item.dataobra="{ value }">{{ formatUnix(value) }}</template>
          <template v-slot:item.u_mata="{ index, value, item }">
            <v-edit-dialog
              large
              persistent
              :cancel-text="$t('close')"
              :save-text="$t('save')"
              @open="openWoods(value)"
              @save="updateUMata({
                index, value: active.wood, field: 'u_mata', bostamp: item.bostamp
              })"
            >
              <wot-list-item :item="value">{{ value }}</wot-list-item>
              <template v-slot:input>
                <validation-provider v-slot="{ errors }" name="u_mata">
                  <v-autocomplete
                    v-model="active.wood"
                    :items="woods"
                    :label="$t('fields.u_mata')"
                    :prepend-icon="icons.mdiAccountSearch"
                    :error-messages="errors"
                    single-line
                    autofocus
                    clearable
                  ></v-autocomplete>
                </validation-provider>
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:item.u_cepos="{ index, value, item }">
            <v-edit-dialog
              large
              persistent
              :cancel-text="$t('close')"
              :save-text="$t('save')"
              @open="val = value"
              @save="inlineSave({
                index, value: val, field: 'u_cepos', bostamp: item.bostamp
              })"
            >
              {{ value }}
              <template v-slot:input>
                <validation-provider v-slot="{ errors }" name="u_cepos" rules="required">
                  <v-text-field
                    v-model="val"
                    :label="`${$t('fields.u_cepos')} *`"
                    :prepend-icon="icons.mdiPencilOutline"
                    :error-messages="errors"
                    single-line
                    autofocus
                    counter="40"
                    maxlength="40"
                  />
                </validation-provider>
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:item.u_quant2="{ index, item, value }">
            <v-edit-dialog
              large
              persistent
              :cancel-text="$t('close')"
              :save-text="$t('save')"
              @open="val = value"
              @save="inlineSave({
                index, value: val, field: 'u_quant2', bostamp: item.bostamp
              })"
            >
              <wot-list-item :item="`${value} ${item.unidade}`">
                {{ item.u_quant2 }} {{ item.unidade }}
              </wot-list-item>
              <template v-slot:input>
                <validation-provider v-slot="{ errors }" name="u_quant2" rules="max_value:99999">
                  <v-text-field
                    v-model.number="val"
                    type="number"
                    :label="$t('fields.u_quant2')"
                    :prepend-icon="icons.mdiPencilOutline"
                    :error-messages="errors"
                    single-line
                    autofocus
                  />
                </validation-provider>
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:item.u_kickback="{ index, item, value }">
            <v-edit-dialog
              v-if="item.agent_number !== null"
              large
              persistent
              :cancel-text="$t('close')"
              :save-text="$t('save')"
              @open="val = value"
              @save="inlineSave({
                index, value: val, field: item.fields_agent_list.u_kickback, bostamp: item.bostamp
              })"
            >
              <wot-list-item :item="value">
                <span :class="!value ? 'warning--text' : ''">{{ $n(value, 'currency') }}</span>
              </wot-list-item>
              <template v-slot:input>
                <validation-provider v-slot="{ errors }" name="u_kickback" rules="max_value:99999">
                  <v-text-field
                    v-model.number="val"
                    type="number"
                    :label="$t('fields.u_kickback')"
                    :prepend-icon="icons.mdiPencilOutline"
                    :error-messages="errors"
                    single-line
                    autofocus
                  />
                </validation-provider>
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:item.total="{ item }">
            {{ $n(item.u_quant2 * item.u_kickback, 'currency') }}
          </template>
          <template v-slot:item.u_uftagent="{ index, item, value }">
            <v-edit-dialog
              v-if="item.agent_number !== null"
              large
              persistent
              :cancel-text="$t('close')"
              :save-text="$t('save')"
              @open="val = value"
              @save="inlineSave({
                index, value: val, field: item.fields_agent_list.u_uftagent, bostamp: item.bostamp
              })"
            >
              <wot-list-item :item="value || 'truthy'">
                <span v-if="value">{{ value }}</span>
                <span class="info--text" v-else>{{ $t('noFt') }}</span>
              </wot-list-item>
              <template v-slot:input>
                <validation-provider v-slot="{ errors }" name="u_uftagent">
                  <v-text-field
                    v-model="val"
                    :label="$t('fields.u_uftagent')"
                    :prepend-icon="icons.mdiPencilOutline"
                    :error-messages="errors"
                    single-line
                    autofocus
                    counter="15"
                    maxlength="15"
                  />
                </validation-provider>
              </template>
            </v-edit-dialog>
          </template>
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <v-btn
              color="primary"
              icon
              :to="{ name: 'editLoadOrder', params: { bostamp: item.bostamp }}"
              target="_blank"
            >
              <v-icon>{{ icons.mdiFileDocumentEditOutline }}</v-icon>
            </v-btn>
            <v-badge v-if="item.anexos > 0" :content="item.anexos" color="secondary" overlap>
              <v-btn icon @click="expand(true)" v-if="!isExpanded">
                <v-icon>{{ icons.mdiChevronDown }}</v-icon>
              </v-btn>
              <v-btn icon @click="expand(false)" v-if="isExpanded">
                <v-icon>{{ icons.mdiChevronUp }}</v-icon>
              </v-btn>
            </v-badge>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <attachments
                class="mt-3 mb-3 text-right"
                :bostamp="item.bostamp"
                @open-pdf="openPdf"
                @reload-list="getAgentList"
              />
            </td>
          </template>
          <template slot="body.append">
            <tr>
              <td colspan="8"></td>
              <td v-for="(item, index) in sumAgentQuant" :key="index">
                {{ displayTotal(item) }}
              </td>
              <td class="text-right">{{ $n(sumAgentTotal, 'currency') }}</td>
              <td></td>
            </tr>
          </template>
          <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
            {{ $t('pageText', [pageStart, pageStop, itemsLength]) }} <br />
            {{ $t('loadOrdersCount', [itemsLength - hiddenStamps.length]) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <wot-send-list
      v-if="emailDialog"
      :count="agentTotal"
      :emails="emails"
      :options="options"
      :keyword="search"
      type="agent"
      :bostamps="bostamps"
    />
    <v-dialog
      v-model="pdfDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      @keydown.esc="pdfDialog = false"
    >
      <v-card>
        <v-toolbar dark color="primary" dense>
          <v-btn icon dark @click="pdfDialog = false"><v-icon>{{ icons.mdiClose }}</v-icon></v-btn>
          <v-toolbar-title>{{ titleDialog }}</v-toolbar-title>
        </v-toolbar>
        <vue-pdf-app :pdf="fileName"></vue-pdf-app>
      </v-card>
    </v-dialog>
    <link rel="resource" type="application/l10n" :href="`/pdf.js/${lang}/viewer.properties`">
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import { ValidationProvider } from 'vee-validate';
import {
  mdiMagnify,
  mdiFileDocumentEditOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiReload,
  mdiClose,
} from '@mdi/js';
import { itemsPerPage } from '@/resources/options';
import { agentListHeaders } from '@/resources/headers';
import functions from '@/resources/functions';

export default {
  metaInfo() {
    return {
      title: this.$t('agentList'),
    };
  },
  mixins: [functions],
  components: {
    ValidationProvider,
    LoadOrdersFilter: () => import(/* webpackChunkName: "filter" */ '@/components/filters/LoadOrdersFilter.vue'),
    WotListItem: () => import(/* webpackChunkName: "wot" */ '@/components/wot/WotListItem.vue'),
    WotFilterMenu: () => import(/* webpackChunkName: "wot" */ '@/components/wot/WotFilterMenu.vue'),
    WotSendList: () => import(/* webpackChunkName: "wot" */ '@/components/wot/WotSendList.vue'),
    Attachments: () => import(/* webpackChunkName: "attachment" */ '../Attachments.vue'),
    VuePdfApp: () => import(/* webpackChunkName: "pdf-viewer" */ 'vue-pdf-app'),
  },
  data() {
    return {
      lang: process.env.VUE_APP_I18N_LOCALE,
      pdfKey: 0,
      itemsPerPage,
      search: '',
      options: {},
      headers: agentListHeaders,
      expanded: [],
      titleDialog: '',
      fileName: null,
      pdfDialog: false,
      icons: {
        mdiMagnify,
        mdiFileDocumentEditOutline,
        mdiChevronDown,
        mdiChevronUp,
        mdiReload,
        mdiClose,
      },
      active: {
        wood: '',
      },
      excelFields: {
        [this.$t('fields.obrano')]: 'obrano',
        [this.$t('fields.dataobra')]: {
          field: 'dataobra',
          callback: (value) => this.formatUnix(value),
        },
        [this.$t('fields.u_cepos')]: 'u_cepos',
        [this.$t('fields.u_matric')]: 'u_matric',
        [this.$t('fields.u_mata')]: 'u_mata',
        [this.$t('fields.u_tipos')]: 'u_tipos',
        [this.$t('fields.u_quant2')]: 'u_quantity',
        [this.$t('fields.u_kickback')]: 'u_kickback',
        [this.$t('fields.total')]: 'total',
      },
    };
  },
  computed: {
    ...mapGetters('loadOrders', ['agentList', 'agentTotal', 'sumAgentTotal', 'sumAgentTotal2', 'sumAgentTotal3', 'filterUAgentEmail', 'emailDialog', 'sumAgentQuant', 'loadOrdersFilter', 'getAgentListValue', 'woodProvider', 'woods']),
    ...mapGetters(['isLoading']),
    ...mapFields('loadOrders', {
      uMataFilter: 'filterMenu.u_mata',
      uTiposFilter: 'filterMenu.u_tipos',
    }),
    rowFilterSets() {
      return {
        u_mata: new Set(this.uMataFilter),
        u_tipos: new Set(this.uTiposFilter),
      };
    },
    hasActiveRowFilters() {
      return Object.values(this.rowFilterSets).some((set) => set.size > 0);
    },
    hiddenStamps() {
      if (!this.hasActiveRowFilters) {
        return [];
      }

      return this.agentList
        .filter((item) => this.shouldHideRow(item))
        .map((item) => item.bostamp);
    },
    hiddenStampsSet() {
      return new Set(this.hiddenStamps);
    },
    emails() {
      return this.explodeEmails(this.filterUAgentEmail);
    },
    bostamps() {
      return this.agentList.filter(({ anexos }) => anexos > 0)
        .map(({ bostamp }) => bostamp);
    },
  },
  watch: {
    options: {
      handler() {
        this.getAgentList();
      },
      deep: true,
    },
    hiddenStamps: {
      handler(value) {
        this.setFilteredStamps(value);
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['fetchServerTime']),
    ...mapActions('loadOrders', ['showAgentList', 'downloadPdf', 'updateOrdersListValue', 'showWoods', 'showWoodProvider']),
    ...mapMutations(['setLoading']),
    ...mapMutations('loadOrders', ['setFilteredStamps']),
    clearFilters() {
      this.uMataFilter = [];
      this.uTiposFilter = [];
    },
    shouldHideRow(item) {
      const sets = this.rowFilterSets;

      return (
        sets.u_mata.has(item.u_mata)
        || sets.u_tipos.has(item.u_tipos)
      );
    },
    rowClasses(item) {
      return this.hiddenStampsSet.has(item.bostamp) ? 'd-none' : '';
    },
    openPdf({ fileName, title }) {
      this.pdfKey += 1;

      this.titleDialog = title;
      this.fileName = fileName;

      this.pdfDialog = true;
    },
    async getAgentList() {
      this.setLoading(true);

      await this.showAgentList({ ...this.options, keyword: this.search });

      this.setLoading(false);
    },
    async emitDownloadPdf() {
      this.setLoading(true);

      this.downloadPdf({ ...this.options, keyword: this.search, type: 'agent' });

      this.setLoading(false);
    },
    async inlineSave(obj) {
      this.setLoading(true);

      await this.updateOrdersListValue({ ...obj, type: 'agent' });

      this.setLoading(false);

      await this.getAgentList();
    },
    async openWoods(val) {
      this.setLoading(true);

      await this.showWoods();

      this.active.wood = val;

      this.setLoading(false);
    },
    async updateUMata(obj) {
      this.setLoading(true);

      const { value } = obj;

      this.updateOrdersListValue({ ...obj, type: 'agent' });

      if (value) {
        await this.showWoodProvider(value);

        await this.updateOrdersListValue({
          ...obj, field: 'u_nmfornec', value: this.woodProvider.nome, com: false,
        });
      }

      this.setLoading(false);
    },
  },
  async mounted() {
    await this.fetchServerTime();
  },
};
</script>
